// src/components/ui/Header/Header.tsx
import React, {useState} from 'react';
import {
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
} from '@ionic/react';
import {
    settingsOutline,
    chevronBackOutline,
    searchOutline,
    filterOutline,
    ellipsisHorizontal,
    notificationsOutline,
} from 'ionicons/icons';
import {useHistory} from 'react-router-dom';

// Header type: Home (logo) vs Page (title)
export type HeaderType = 'home' | 'page';

// Text alignment options
export type TextAlign = 'left' | 'center' | 'right';

// Background style presets
export type BackgroundStyle = 'default' | 'transparent' | 'branded' | 'elevated' | 'subtle';

// Background configuration
interface BackgroundConfig {
    style?: BackgroundStyle;
    customColor?: string;
    customGradient?: string;
}

interface HeaderProps {
    /** Header type: 'home' for logo-based, 'page' for title-based */
    type?: HeaderType;

    // ═══════════════════════════════════════════
    // PAGE HEADER ADDONS
    // ═══════════════════════════════════════════

    /** Page title text */
    title?: string;
    /** Title alignment */
    titleAlign?: TextAlign;
    /** Subtitle text */
    subtitle?: string;
    /** Subtitle alignment (defaults to titleAlign) */
    subtitleAlign?: TextAlign;

    // ═══════════════════════════════════════════
    // BACKGROUND ADDON
    // ═══════════════════════════════════════════

    /** Background configuration */
    background?: BackgroundConfig;

    // ═══════════════════════════════════════════
    // ICON ADDONS (Both Home & Page)
    // ═══════════════════════════════════════════

    /** Show back button (page type only) */
    showBack?: boolean;
    /** Custom back action */
    onBack?: () => void;
    /** Show notifications icon */
    showNotifications?: boolean;
    /** Notification count badge */
    notificationCount?: number;
    /** Notifications click handler */
    onNotifications?: () => void;
    /** Show settings icon */
    showSettings?: boolean;
    /** Show search icon */
    showSearch?: boolean;
    /** Search click handler */
    onSearch?: () => void;
    /** Show filter icon */
    showFilter?: boolean;
    /** Filter click handler */
    onFilter?: () => void;
    /** Show more options (ellipsis) */
    showMore?: boolean;
    /** More options click handler */
    onMore?: () => void;
    /** Custom right-side buttons */
    rightButtons?: React.ReactNode;
}

// Background style configurations
const backgroundStyles: Record<BackgroundStyle, {
    background: string;
    textColor: string;
    iconColor: string;
    borderBottom?: string;
    boxShadow?: string;
}> = {
    default: {
        background: 'transparent',
        textColor: 'var(--ion-text-color)',
        iconColor: 'var(--ion-text-color)',
        borderBottom: '1px solid var(--ion-border-color)',
    },
    transparent: {
        background: 'transparent',
        textColor: 'var(--ion-text-color)',
        iconColor: 'var(--ion-text-color)',
    },
    branded: {
        background: 'linear-gradient(135deg, var(--kff-foundry-blue) 0%, #17334f 100%)',
        textColor: '#ffffff',
        iconColor: '#ffffff',
        boxShadow: '0 4px 20px rgba(27, 59, 90, 0.4)',
    },
    elevated: {
        background: 'var(--ion-card-background)',
        textColor: 'var(--ion-text-color)',
        iconColor: 'var(--ion-text-color)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        borderBottom: '1px solid var(--ion-border-color)',
    },
    subtle: {
        background: 'var(--ion-background-color)',
        textColor: 'var(--ion-text-color)',
        iconColor: 'var(--ion-color-medium)',
    },
};

export const Header: React.FC<HeaderProps> = ({
    type = 'page',
    title,
    titleAlign = 'left',
    subtitle,
    subtitleAlign,
    background = {style: 'default'},
    showBack = false,
    onBack,
    showNotifications = false,
    notificationCount,
    onNotifications,
    showSettings = false,
    showSearch = false,
    onSearch,
    showFilter = false,
    onFilter,
    showMore = false,
    onMore,
    rightButtons,
}) => {
    const history = useHistory();
    const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);

    const isHome = type === 'home';
    const effectiveSubtitleAlign = subtitleAlign || titleAlign;

    // Resolve background styles
    const bgStyle = background.style || 'default';
    const baseStyles = backgroundStyles[bgStyle];
    const resolvedBackground = background.customGradient
        || background.customColor
        || baseStyles.background;

    // Determine if we're using a dark background (for text contrast)
    const isDarkBg = bgStyle === 'branded' || background.customColor?.startsWith('#1') || background.customColor?.startsWith('#2');
    const textColor = isDarkBg ? '#ffffff' : baseStyles.textColor;
    const iconColor = isDarkBg ? '#ffffff' : baseStyles.iconColor;
    const subtitleColor = isDarkBg ? 'rgba(255,255,255,0.7)' : 'var(--ion-color-medium)';

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            history.goBack();
        }
    };

    // Icon button with hover state and optional badge
    const IconButton: React.FC<{
        icon: string;
        onClick?: () => void;
        ariaLabel: string;
        id: string;
        badge?: number;
    }> = ({icon, onClick, ariaLabel, id, badge}) => (
        <IonButton
            fill="clear"
            onClick={onClick}
            aria-label={ariaLabel}
            onMouseEnter={() => setHoveredIcon(id)}
            onMouseLeave={() => setHoveredIcon(null)}
            style={{
                '--color': iconColor,
                position: 'relative',
                transition: 'all 0.2s ease',
                transform: hoveredIcon === id ? 'scale(1.1)' : 'scale(1)',
            }}
        >
            <IonIcon
                slot="icon-only"
                icon={icon}
                style={{
                    fontSize: 22,
                    transition: 'color 0.2s',
                    color: hoveredIcon === id ? 'var(--kff-forge-gold)' : iconColor,
                }}
            />
            {badge !== undefined && badge > 0 && (
                <span
                    style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        minWidth: badge > 9 ? 14 : 8,
                        height: badge > 9 ? 14 : 8,
                        borderRadius: 7,
                        background: 'var(--kff-alert-red, #e53e3e)',
                        color: '#fff',
                        fontSize: 9,
                        fontWeight: 700,
                        fontFamily: 'var(--kff-data-font)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: badge > 9 ? '0 3px' : 0,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        lineHeight: 1,
                    }}
                >
                    {badge > 9 ? (badge > 99 ? '99+' : badge) : ''}
                </span>
            )}
        </IonButton>
    );

    // Title component with alignment
    const TitleBlock: React.FC<{align: TextAlign; inSlot?: boolean}> = ({align, inSlot = false}) => (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
                textAlign: align,
                flex: align === 'center' ? 1 : undefined,
                paddingLeft: inSlot ? 0 : (showBack ? 8 : 16),
                paddingRight: align === 'right' ? 16 : 0,
                minHeight: 44, // Match icon button height for vertical alignment
            }}
        >
            <div
                style={{
                    fontFamily: 'var(--kff-heading-font)',
                    fontSize: 18,
                    fontWeight: 700,
                    color: textColor,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                }}
            >
                {title}
            </div>
            {subtitle && (
                <span
                    style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 12,
                        color: subtitleColor,
                        marginTop: 2,
                        lineHeight: 1.2,
                    }}
                >
                    {subtitle}
                </span>
            )}
        </div>
    );

    // Determine where to render title based on alignment and back button
    const renderLeftTitle = !isHome && title && titleAlign === 'left';
    const renderCenterTitle = !isHome && title && titleAlign === 'center';
    const renderRightTitle = !isHome && title && titleAlign === 'right';

    return (
        <IonHeader
            style={{
                '--background': resolvedBackground,
            }}
        >
            <IonToolbar
                style={{
                    '--background': resolvedBackground,
                    '--color': textColor,
                    '--border-color': 'transparent',
                    borderBottom: baseStyles.borderBottom || 'none',
                    boxShadow: baseStyles.boxShadow || 'none',
                    transition: 'all 0.3s ease',
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {/* ═══════════════════════════════════════════ */}
                {/* LEFT SIDE */}
                {/* ═══════════════════════════════════════════ */}
                <IonButtons slot="start">
                    {/* Back button (page type only) */}
                    {!isHome && showBack && (
                        <IconButton
                            id="back"
                            icon={chevronBackOutline}
                            onClick={handleBack}
                            ariaLabel="Go back"
                        />
                    )}

                    {/* Home: Logo */}
                    {isHome && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8,
                                transition: 'transform 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            <img
                                src="/Images/KFF_horizontal_logo_SM.png"
                                alt="Kelly Foundry Finance"
                                style={{
                                    height: 28,
                                    width: 'auto',
                                }}
                            />
                        </div>
                    )}
                </IonButtons>

                {/* ═══════════════════════════════════════════ */}
                {/* TITLE AREA */}
                {/* ═══════════════════════════════════════════ */}
                {/* Page: Left-aligned title (rendered directly in toolbar for proper alignment) */}
                {renderLeftTitle && <TitleBlock align="left" />}

                {/* Page: Center-aligned title */}
                {renderCenterTitle && <TitleBlock align="center" />}

                {/* Page: Right-aligned title */}
                {renderRightTitle && <TitleBlock align="right" />}

                {/* ═══════════════════════════════════════════ */}
                {/* RIGHT SIDE */}
                {/* ═══════════════════════════════════════════ */}
                <IonButtons slot="end">
                    {rightButtons}

                    {showSearch && (
                        <IconButton
                            id="search"
                            icon={searchOutline}
                            onClick={onSearch}
                            ariaLabel="Search"
                        />
                    )}
                    {showFilter && (
                        <IconButton
                            id="filter"
                            icon={filterOutline}
                            onClick={onFilter}
                            ariaLabel="Filter"
                        />
                    )}
                    {showMore && (
                        <IconButton
                            id="more"
                            icon={ellipsisHorizontal}
                            onClick={onMore}
                            ariaLabel="More options"
                        />
                    )}
                    {showNotifications && (
                        <IconButton
                            id="notifications"
                            icon={notificationsOutline}
                            onClick={onNotifications}
                            ariaLabel="Notifications"
                            badge={notificationCount}
                        />
                    )}
                    {showSettings && (
                        <IconButton
                            id="settings"
                            icon={settingsOutline}
                            onClick={() => history.push('/settings')}
                            ariaLabel="Settings"
                        />
                    )}
                </IonButtons>
            </IonToolbar>
        </IonHeader>
    );
};

export default Header;
